import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, PauseCircle, Star, ClipboardList, Trophy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { RESULTS } from "@/lib/constants";

const COLORS = { "الرياض": "#1B2F8F", "الدمام": "#D7262E" };
const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (m) => `${AR_MONTHS[+m.slice(5) - 1]} ${m.slice(0, 4)}`;
const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Riyadh" }).slice(0, 7);

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
    <p className="text-xs text-slate-500 flex items-center gap-1"><Icon size={13} />{label}</p>
    <p className="font-mono text-2xl font-bold mt-1" data-testid={testId}>{value}</p>
  </div>
);

const BranchCard = ({ b }) => {
  const max = Math.max(1, ...Object.values(b.outcomes));
  const outcomes = [...RESULTS, "بدون نتيجة"].filter((o) => b.outcomes[o]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid={`report-branch-${b.branch}`}>
      <h2 className="font-heading text-2xl font-extrabold flex items-center gap-2"><span className="h-4 w-4 rounded-full" style={{ background: COLORS[b.branch] }} />فرع {b.branch}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi icon={CheckCircle2} label="مكتملة" value={b.completed} testId={`report-completed-${b.branch}`} />
        <Kpi icon={ClipboardList} label="أُسندت هذا الشهر" value={b.created} testId={`report-created-${b.branch}`} />
        <Kpi icon={PauseCircle} label="معلقة" value={b.on_hold} />
        <Kpi icon={Star} label="متوسط التقييم" value={b.avg_rating != null ? `${b.avg_rating}` : "—"} />
      </div>
      <div className="space-y-2">
        <p className="text-sm font-bold">نتائج المعاينات المكتملة</p>
        {outcomes.length === 0 && <p className="text-sm text-slate-500">لا توجد معاينات مكتملة في هذا الشهر</p>}
        {outcomes.map((o) => (
          <div key={o} className="text-sm" data-testid={`report-outcome-${b.branch}-${o}`}>
            <div className="flex justify-between mb-1"><span>{o}</span><span className="font-mono font-bold">{b.outcomes[o]}</span></div>
            <div className="h-2.5 rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${(b.outcomes[o] / max) * 100}%`, background: COLORS[b.branch] }} /></div>
          </div>
        ))}
      </div>
      {b.top_agents.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-bold flex items-center gap-1"><Trophy size={14} className="text-amber-500" />أكثر المناديب إنجازاً</p>
          {b.top_agents.map((a) => <p key={a.name} className="text-sm flex justify-between"><span>{a.name}</span><span className="font-mono">{a.completed}</span></p>)}
        </div>
      )}
    </div>
  );
};

const Trend = ({ data }) => {
  const max = Math.max(1, ...data.branches.flatMap((b) => Object.values(data.trend[b.branch])));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="report-trend">
      <p className="font-heading font-bold mb-4">المعاينات المكتملة آخر 6 أشهر</p>
      <div className="grid grid-cols-6 gap-3 items-end h-48">
        {data.months.map((m) => (
          <div key={m} className="flex flex-col items-center gap-1 h-full justify-end">
            <div className="flex items-end gap-1 h-full w-full justify-center">
              {data.branches.map((b) => {
                const v = data.trend[b.branch][m];
                return <div key={b.branch} title={`${b.branch}: ${v}`} className="w-5 rounded-t relative" style={{ height: `${(v / max) * 100}%`, minHeight: 2, background: COLORS[b.branch] }}>
                  {v > 0 && <span className="absolute -top-5 inset-x-0 text-center text-[11px] font-mono font-bold">{v}</span>}
                </div>;
              })}
            </div>
            <span className="text-[11px] text-slate-500 whitespace-nowrap">{monthLabel(m)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-3 text-sm">{data.branches.map((b) => <span key={b.branch} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded" style={{ background: COLORS[b.branch] }} />{b.branch}</span>)}</div>
    </div>
  );
};

export default function BranchReport() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/branches", { params: { month } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  return (
    <div className="space-y-6" data-testid="branch-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1">مقارنة شهرية</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">تقرير الفروع</h1>
        </div>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-48 h-11" data-testid="report-month-input" />
      </div>
      {data && (
        <>
          <div className="grid lg:grid-cols-2 gap-4">{data.branches.map((b) => <BranchCard key={b.branch} b={b} />)}</div>
          <Trend data={data} />
        </>
      )}
    </div>
  );
}
